import { useState, useEffect, useCallback } from 'react';
import { summaryText } from '@/lib/eventSummary';
import { useNavigate } from 'react-router-dom';
import { eventsApi } from '@/lib/api';
import { useWebSocket, useRealtimeEvent } from '@/context/WebSocketContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Bell, BellOff, Volume2, VolumeX, AlertTriangle, Brain, Activity, CheckCircle, Clock, ChevronRight, Download, Share, Smartphone } from 'lucide-react';
import { cn, utcDate } from '@/lib/utils';
import { format, formatDistanceToNow } from 'date-fns';
import { sv } from 'date-fns/locale';
import { toast } from 'sonner';
import { TestPushButton } from '@/components/TestPushButton';

const severityConfig = {
  high: { label: 'HÖG', color: 'bg-red-600 text-white', ring: 'ring-red-400', bg: 'bg-red-50 border-red-300' },
  medium: { label: 'MEDEL', color: 'bg-orange-500 text-white', ring: 'ring-orange-400', bg: 'bg-orange-50 border-orange-200' },
  low: { label: 'LÅG', color: 'bg-slate-400 text-white', ring: 'ring-slate-300', bg: 'bg-slate-50 border-slate-200' },
};

const typeLabels = {
  vape: 'VAPE',
  suspect: 'MISSTÄNKT',
  offline: 'OFFLINE',
  test_alarm: 'TESTLARM',
};

// Detect iOS
const isIOS = () => /iPhone|iPad|iPod/.test(navigator.userAgent);
// Detect if running as installed PWA
const isStandalone = () =>
  window.navigator.standalone === true ||
  window.matchMedia('(display-mode: standalone)').matches;

function timeAgo(timestamp) {
  if (!timestamp) return '';
  try {
    return formatDistanceToNow(utcDate(timestamp), { addSuffix: true, locale: sv });
  } catch {
    return '';
  }
}

export default function NotificationsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem('va_sound') !== 'off');
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const navigate = useNavigate();

  const pushSupported = 'Notification' in window;
  const onIOS = isIOS();
  const isPWA = isStandalone();

  useEffect(() => {
    loadEvents();
    checkPushPermission();

    // Show iOS guide if on iOS but not installed as PWA
    if (isIOS() && !isStandalone()) {
      setShowIOSGuide(true);
    }

    // Capture PWA install prompt (Chrome/Android only)
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadEvents = async () => {
    try {
      const res = await eventsApi.list({ limit: 50 });
      setEvents(res.data);
    } catch {
      toast.error('Kunde inte ladda notifikationer');
    } finally {
      setLoading(false);
    }
  };

  const checkPushPermission = () => {
    if ('Notification' in window) {
      setPushEnabled(Notification.permission === 'granted');
    }
  };

  const togglePush = async () => {
    if (!pushSupported) {
      if (onIOS && !isPWA) {
        setShowIOSGuide(true);
        toast.info('Installera appen först — se instruktionerna nedan');
      } else {
        toast.error('Din webbläsare stöder inte push-notiser');
      }
      return;
    }
    if (Notification.permission === 'granted') {
      setPushEnabled(false);
      toast.info('Push-notiser avstängda (ladda om sidan för att återaktivera)');
      return;
    }
    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      setPushEnabled(true);
      toast.success('Push-notiser aktiverade!');
      new Notification('Vapelarm Cloud', { body: 'Push-notiser är nu aktiverade.', icon: '/favicon.ico' });
    } else {
      toast.error('Push-notiser nekade av webbläsaren');
    }
  };

  const toggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    localStorage.setItem('va_sound', next ? 'on' : 'off');
    toast.info(next ? 'Ljud aktiverat' : 'Ljud avstängt');
  };

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === 'accepted') {
      toast.success('Vapelarm Cloud installerad på hemskärmen!');
    }
    setInstallPrompt(null);
  };

  // Real-time: prepend new events
  useRealtimeEvent('new_event', useCallback((data) => {
    if (data.event) {
      setEvents(prev => [data.event, ...prev.slice(0, 49)]);
    }
  }, []));

  if (loading) {
    return (
      <div className="p-4 md:p-10 flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-slate-500 font-ibm">Laddar notifikationer...</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-10 space-y-4 max-w-2xl mx-auto" data-testid="notifications-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-chivo font-black text-2xl text-navy tracking-tight">
            Notifikationer
          </h1>
          <p className="text-slate-500 text-sm font-ibm mt-0.5">
            Realtidslarm från alla enheter
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={pushEnabled ? "default" : "outline"}
            size="sm"
            onClick={togglePush}
            className={cn("text-xs", pushEnabled && "bg-navy")}
            data-testid="push-toggle-btn"
          >
            {pushEnabled ? <Bell className="h-3.5 w-3.5 mr-1" /> : <BellOff className="h-3.5 w-3.5 mr-1" />}
            Push
          </Button>
          <Button
            variant={soundEnabled ? "default" : "outline"}
            size="sm"
            onClick={toggleSound}
            className={cn("text-xs", soundEnabled && "bg-navy")}
            data-testid="sound-toggle-btn"
          >
            {soundEnabled ? <Volume2 className="h-3.5 w-3.5 mr-1" /> : <VolumeX className="h-3.5 w-3.5 mr-1" />}
            Ljud
          </Button>
        </div>
      </div>

      <Card className="border-slate-200" data-testid="test-push-card">
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <p className="font-ibm font-semibold text-sm text-navy">Fungerar pushnotiserna?</p>
            <p className="text-xs text-slate-500 mt-0.5">Skicka en testnotis till din egen telefon. Inget larm skapas.</p>
          </div>
          <TestPushButton size="sm" testId="test-push-btn-notifications" />
        </CardContent>
      </Card>

      {/* iOS Install Guide */}
      {showIOSGuide && (
        <Card className="border-blue-200 bg-blue-50/70" data-testid="ios-install-guide">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center shrink-0">
                <Smartphone className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-ibm font-semibold text-sm text-blue-900">
                  Installera Vapelarm Cloud för push-notiser
                </p>
                <p className="text-xs text-blue-700 mt-1 leading-relaxed">
                  På iPhone behöver du lägga till appen på hemskärmen för att aktivera push-notiser:
                </p>
                <ol className="text-xs text-blue-700 mt-2 space-y-1.5 list-none">
                  <li className="flex items-start gap-2">
                    <span className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold shrink-0">1</span>
                    <span>Tryck på <Share className="inline h-3.5 w-3.5 mx-0.5 -mt-0.5" /> <strong>Dela-knappen</strong> längst ner i Safari</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold shrink-0">2</span>
                    <span>Scrolla ner och tryck <strong>"Lägg till på hemskärmen"</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold shrink-0">3</span>
                    <span>Öppna Vapelarm Cloud från hemskärmen och aktivera push-notiser</span>
                  </li>
                </ol>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowIOSGuide(false)}
              className="mt-2 text-blue-600 hover:text-blue-800 text-xs w-full"
            >
              Dölj instruktioner
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Push not supported (non-iOS) */}
      {!pushSupported && !onIOS && (
        <Card className="border-slate-200 bg-slate-50" data-testid="push-not-supported">
          <CardContent className="p-3 flex items-center gap-2">
            <BellOff className="h-4 w-4 text-slate-400 shrink-0" />
            <p className="text-xs text-slate-500 font-ibm">
              Din webbläsare stöder inte push-notiser. Prova Chrome eller Edge.
            </p>
          </CardContent>
        </Card>
      )}

      {/* PWA Install Banner (Chrome/Android) */}
      {installPrompt && (
        <Card className="border-navy bg-navy/5" data-testid="pwa-install-banner">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-navy flex items-center justify-center">
                <Download className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="font-ibm font-semibold text-sm text-navy">Installera Vapelarm Cloud</p>
                <p className="text-xs text-slate-500">Lägg till på hemskärmen för snabbare åtkomst</p>
              </div>
            </div>
            <Button size="sm" onClick={handleInstall} className="bg-navy" data-testid="pwa-install-btn">
              Installera
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Event cards - mobile optimized */}
      <div className="space-y-2">
        {events.map((event) => {
          const sev = severityConfig[event.severity] || {};
          const isUrgent = event.severity === 'high' || event.event_type === 'vape';
          const isOpen = event.status === 'open';

          return (
            <button
              key={event.id}
              onClick={() => navigate(`/devices/${event.device_id}`)}
              className={cn(
                "w-full text-left rounded-lg border p-3.5 transition-all active:scale-[0.98]",
                isUrgent && isOpen ? "border-red-300 bg-red-50" :
                event.severity === 'medium' && isOpen ? "border-orange-200 bg-orange-50/70" :
                event.event_type === 'offline' ? "border-slate-200 bg-slate-50" :
                "border-slate-200 bg-white",
                !isOpen && "opacity-60"
              )}
              data-testid={`notification-${event.id}`}
            >
              <div className="flex items-start gap-3">
                {/* Left icon */}
                <div className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center shrink-0 mt-0.5",
                  isUrgent ? "bg-red-600" : event.severity === 'medium' ? "bg-orange-500" : "bg-slate-400"
                )}>
                  <AlertTriangle className="h-5 w-5 text-white" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={cn(
                      "text-xs font-bold uppercase tracking-wide",
                      isUrgent ? "text-red-700" : event.event_type === 'suspect' ? "text-amber-700" : "text-slate-600"
                    )}>
                      {typeLabels[event.event_type] || event.event_type}
                    </span>
                    {event.severity && (
                      <span className={cn("text-[10px] font-bold uppercase px-1.5 py-0.5 rounded", sev.color)}>
                        {sev.label}
                      </span>
                    )}
                    {event.detection_method === 'ml' && (
                      <Brain className="h-3 w-3 text-violet-500" />
                    )}
                    {!isOpen && (
                      <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                        {event.status === 'resolved' ? 'LÖST' : event.status === 'acknowledged' ? 'BEKRÄFTAD' : event.status === 'false_positive' ? 'FALSKT POSITIV' : event.status?.toUpperCase()}
                      </span>
                    )}
                  </div>

                  <p className="font-ibm font-semibold text-sm text-slate-900 mt-0.5 truncate">
                    {event.device_name}
                  </p>
                  <p className="text-xs text-slate-500 truncate">
                    {event.school_name} — {event.room_name}
                  </p>
                  <p className="text-xs text-slate-600 mt-1 line-clamp-2">
                    {summaryText(event)}
                  </p>
                </div>

                {/* Right side */}
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <span className="text-[11px] text-slate-400 tabular-nums whitespace-nowrap">
                    {timeAgo(event.created_at)}
                  </span>
                  <span className="text-xs font-bold tabular-nums text-slate-600">
                    {(event.score * 100).toFixed(0)}%
                  </span>
                  <ChevronRight className="h-4 w-4 text-slate-300 mt-1" />
                </div>
              </div>
            </button>
          );
        })}

        {events.length === 0 && (
          <Card className="border-slate-200">
            <CardContent className="py-16 text-center">
              <Bell className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-chivo text-lg text-slate-700">Inga notifikationer</h3>
              <p className="text-slate-500 mt-1 font-ibm text-sm">
                Du kommer att se larm här när de uppstår
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
